/**
 * Record Flow - how records move through PA, Manifest, UMED and Finance, and
 * where they get stuck. The measuring instrument for the data-cleanup
 * initiative (docs/record-flow/api-contract.md). Visual system: Tide
 * (docs/design/tide.md).
 *
 *   Overview     how much moved, who did it, what is open, what cleanup closed
 *   Bottlenecks  open records by stage and age, dwell time, ranked stages
 *   Flow         from -> to transitions for one domain
 *   People       activity and holdings per person
 *   Providers    provider sign-in, coverage health and today's planned route
 *   Records      the step ledger, with a resident timeline
 *   Atlas        where across Florida the open work sits (atlas/AtlasView)
 *
 * The window and origin filter live here so every tab reads the same slice.
 * The header band states the purpose, the window and when the visible tab's
 * figures were generated, in the window's time zone.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { WindowParams } from './flowApi';
import { ORIGINS, ORIGIN_COLOR, ORIGIN_LABEL, ORIGIN_MARKER } from './chartTheme';
import { Freshness, FreshnessProvider, addDays, fmtGenerated, fmtIsoDate, nyToday, spanDays } from './flowUi';
import OverviewView from './OverviewView';
import BottlenecksView from './BottlenecksView';
import FlowView from './FlowView';
import PeopleView from './PeopleView';
import ProvidersView from './ProvidersView';
import RecordsView, { FacilityFilter, PersonFilter } from './RecordsView';
import AtlasView from './atlas/AtlasView';
import { Segmented } from '../../../components/tide';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/shadcn/ui/tabs';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'bottlenecks', label: 'Bottlenecks' },
  { key: 'flow', label: 'Flow' },
  { key: 'people', label: 'People' },
  { key: 'providers', label: 'Providers' },
  { key: 'records', label: 'Records' },
  { key: 'atlas', label: 'Atlas' },
] as const;
type TabKey = typeof TABS[number]['key'];

const PRESETS = [7, 30, 90] as const;
const MAX_SPAN = 180;
/** The zone the contract expresses every window in. */
const WINDOW_TZ = 'America/New_York';

const ORIGIN_CHIPS = [
  { value: '', label: 'All' },
  ...ORIGINS.map((o) => ({ value: o as string, label: ORIGIN_LABEL[o], marker: { shape: ORIGIN_MARKER[o], color: ORIGIN_COLOR[o] } })),
];

/** Which tabs the Origin filter reaches: the server only filters /flows and /steps. */
const ORIGIN_TABS: TabKey[] = ['flow', 'records'];

const presetWindow = (days: number): WindowParams => {
  const to = nyToday();
  return { from: addDays(to, -(days - 1)), to };
};

export default function RecordFlow() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key || 'overview') as TabKey;
  const setTab = (k: TabKey) => { const p = new URLSearchParams(params); p.set('tab', k); setParams(p, { replace: true }); };

  const [win, setWin] = useState<WindowParams>(() => presetWindow(30));
  const [preset, setPreset] = useState<number | 'custom'>(30);
  const [draft, setDraft] = useState<WindowParams>(win);
  const [windowError, setWindowError] = useState<string | null>(null);
  const [origin, setOrigin] = useState('');
  const [person, setPerson] = useState<PersonFilter | null>(null);
  const [facility, setFacility] = useState<FacilityFilter | null>(null);
  const [freshness, setFreshness] = useState<Freshness | null>(null);
  // Phones only (CSS hides the toggle from 640px up): the window/origin controls fold into one line.
  const [controlsOpen, setControlsOpen] = useState(false);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  // Keep the active tab inside the scrolling tab strip. Only the strip scrolls, never the page.
  useEffect(() => {
    const strip = tabsRef.current;
    const active = strip?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!strip || !active || typeof active.getBoundingClientRect !== 'function') return;
    const s = strip.getBoundingClientRect();
    const r = active.getBoundingClientRect();
    if (r.left < s.left) strip.scrollLeft += r.left - s.left - 16;
    else if (r.right > s.right) strip.scrollLeft += r.right - s.right + 16;
  }, [tab]);

  const choosePreset = (n: number) => {
    const w = presetWindow(n);
    setWin(w); setDraft(w); setPreset(n); setWindowError(null);
  };

  const editDate = (field: 'from' | 'to', value: string) => {
    const next = { ...draft, [field]: value };
    setDraft(next);
    if (!next.from || !next.to) { setWindowError('Choose both a start and an end date.'); return; }
    if (next.from > next.to) { setWindowError('The start date must be on or before the end date.'); return; }
    if (spanDays(next.from, next.to) > MAX_SPAN) { setWindowError(`The window can be at most ${MAX_SPAN} days.`); return; }
    setWindowError(null);
    setWin(next);
    setPreset('custom');
  };

  const selectPerson = (id: string, name: string) => { setPerson({ id, name }); setTab('records'); };
  const showFacility = (f: FacilityFilter) => { setFacility({ id: f.id, name: f.name }); setTab('records'); };
  const days = spanDays(win.from, win.to);
  const originWords = origin ? ORIGIN_LABEL[origin as keyof typeof ORIGIN_LABEL] : 'All origins';

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)} activationMode="manual" className="tide space-y-5">
      <header className="tide-band">
        <div className="tide-band__eyebrow">Data cleanup · measuring instrument</div>
        <h1 className="tide-band__title">Record Flow</h1>
        <p className="tide-band__purpose tide-band__purpose--full">How records move through PA, Manifest, UMED and Finance - and where they get stuck.</p>
        <p className="tide-band__purpose tide-band__purpose--short">Where records move, and where they get stuck.</p>
        <p className="tide-band__meta">
          <span>Window <strong>{fmtIsoDate(win.from)} – {fmtIsoDate(win.to)}</strong> ({days} {days === 1 ? 'day' : 'days'}<span className="tide-band__tz">, {WINDOW_TZ}</span>)</span>
          <span data-testid="rf-freshness">
            {freshness
              ? <>Generated <strong>{fmtGenerated(freshness.generatedAt, freshness.timeZone)}</strong><span className="tide-band__tz"> ({freshness.timeZone})</span></>
              : 'Generated time appears once this tab has loaded.'}
          </span>
        </p>

        <div className="tide-band__summary">
          <span data-testid="rf-controls-summary">{days} {days === 1 ? 'day' : 'days'} · {originWords}</span>
          <button
            type="button"
            className="tide-btn"
            aria-expanded={controlsOpen}
            aria-controls="rf-controls"
            onClick={() => setControlsOpen((o) => !o)}
          >Change</button>
        </div>

        <div className="tide-band__controls" id="rf-controls" data-open={controlsOpen ? 'true' : 'false'}>
          <div role="group" aria-label="Window" className="flex flex-wrap items-end gap-3">
            <Segmented
              label="Last"
              options={PRESETS.map((n) => ({ value: String(n), label: `${n}d` }))}
              value={preset === 'custom' ? null : String(preset)}
              onChange={(v) => choosePreset(Number(v))}
            />
            <label className="tide-field">
              From
              <input type="date" className="tide-input" value={draft.from} max={draft.to || undefined} onChange={(e) => editDate('from', e.target.value)} />
            </label>
            <label className="tide-field">
              To
              <input type="date" className="tide-input" value={draft.to} min={draft.from || undefined} onChange={(e) => editDate('to', e.target.value)} />
            </label>
          </div>
          <Segmented label="Origin" options={ORIGIN_CHIPS} value={origin} onChange={setOrigin} />
        </div>
        {windowError && <p role="alert" className="tide-band__alert">{windowError}</p>}
        {origin && !ORIGIN_TABS.includes(tab) && (
          <p className="tide-band__note">The origin filter applies to Flow and Records. This tab always shows every origin; the Overview shows the split.</p>
        )}

        {/* shadcn Tabs (Radix): one tab in the Tab order, arrows / Home / End move between tabs. Tide's band styles
            follow aria-selected; the ring is the on-hero focus colour. */}
        <TabsList aria-label="Record Flow" className="tide-tabs h-auto w-full justify-start rounded-none bg-transparent p-0 text-inherit" ref={tabsRef}>
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key} value={t.key}
              onClick={() => setTab(t.key)}
              className="tide-tab rounded-none shadow-none data-[state=active]:bg-transparent data-[state=active]:shadow-none focus-visible:ring-[var(--tide-focus-on-hero)] focus-visible:ring-offset-0"
            >{t.label}</TabsTrigger>
          ))}
        </TabsList>
      </header>

      <FreshnessProvider value={setFreshness}>
        <TabsContent value={tab} key={tab} className="tide-panel-in mt-0 focus-visible:ring-offset-0">
          {tab === 'overview' && <OverviewView window={win} />}
          {tab === 'bottlenecks' && <BottlenecksView window={win} />}
          {tab === 'flow' && <FlowView window={win} origin={origin} />}
          {tab === 'people' && <PeopleView window={win} onSelectPerson={selectPerson} />}
          {tab === 'providers' && <ProvidersView window={win} onSelectPerson={selectPerson} />}
          {tab === 'records' && <RecordsView window={win} origin={origin} onOrigin={setOrigin} person={person} onPerson={setPerson} facility={facility} onFacility={setFacility} />}
          {tab === 'atlas' && <AtlasView window={win} onShowRecords={showFacility} />}
        </TabsContent>
      </FreshnessProvider>
    </Tabs>
  );
}
